"use client";

import { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetClose, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-7">
        <div className="h-5 w-20 bg-muted animate-pulse rounded-sm" />
        <div className="h-5 w-24 bg-muted animate-pulse rounded-sm" />
        <div className="h-5 w-20 bg-muted animate-pulse rounded-sm" />
        <div className="h-5 w-16 bg-muted animate-pulse rounded-sm" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-7">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="relative inline-flex h-auto items-center bg-transparent px-0 py-1 text-[0.95rem] font-medium text-foreground tracking-[0.01em] hover:bg-transparent hover:text-foreground focus:bg-transparent focus:text-foreground after:absolute after:left-0 after:-bottom-1.5 after:h-px after:w-0 after:bg-[var(--chart-2)] after:transition-all after:duration-200 hover:after:w-full">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onItemClick
}: {
  onItemClick: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 px-6 py-4">
        <div className="h-7 w-32 bg-muted animate-pulse rounded-sm" />
        <div className="h-7 w-36 bg-muted animate-pulse rounded-sm" />
        <div className="h-7 w-28 bg-muted animate-pulse rounded-sm" />
        <div className="h-7 w-24 bg-muted animate-pulse rounded-sm" />
      </div>;
  }
  return <nav className="flex flex-col px-6 py-2">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} onClick={onItemClick} className="py-3 text-[1.4rem] font-medium text-foreground tracking-[0.005em] border-b border-border last:border-b-0 hover:text-[var(--chart-2)] transition-colors duration-200">
          {item.label}
        </Link>)}
    </nav>;
}
export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="11639" id="navigation-header" className={`sticky top-0 z-50 w-full transition-all duration-[250ms] ease-out bg-background text-foreground ${scrolled ? "bg-background/75 backdrop-blur-lg border-b border-border" : "bg-background border-b border-transparent"}`}>
      <div className="mx-auto max-w-[1280px] px-6 md:px-20">
        <div className="flex h-[72px] items-center justify-between">
          <Link to="/" className="flex items-center">
            <WvcLogo className="h-7 w-auto" />
          </Link>

          {}
          <div className="hidden md:flex flex-1 justify-center">
            <MenuProvider menu_id="16">
              <DesktopMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div className="hidden md:flex items-center justify-end">
            <Button asChild variant="outline" className="inline-flex items-center justify-center gap-2 whitespace-nowrap transition-all [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive border shadow-xs py-2 has-[>svg]:px-3 h-11 rounded-lg border-primary px-6 text-[0.95rem] font-semibold tracking-[0.02em] text-foreground !bg-[rgba(239,67,67,1)] disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 dark:bg-input/30 dark:border-input dark:hover:bg-input/50 hover:bg-primary hover:text-white">
              <Link to="#contact">Contact us today for FREE consultation</Link>
            </Button>
          </div>

          {}
          <div className="flex md:hidden items-center">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="text-foreground hover:bg-muted" aria-label="Open menu">
                  <Menu className="size-6" strokeWidth={1.5} />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full sm:max-w-sm bg-background flex flex-col p-0">
                <SheetHeader className="border-b border-border px-6 py-5">
                  <SheetTitle className="text-left">
                    <span className="text-[0.78rem] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                      Navigation
                    </span>
                  </SheetTitle>
                </SheetHeader>

                <div className="flex-1 overflow-y-auto pt-4">
                  <MenuProvider menu_id="16">
                    <MobileMenuConsumer onItemClick={() => setMobileOpen(false)} />
                  </MenuProvider>
                </div>

                <div className="border-t border-border px-6 py-6">
                  <SheetClose asChild>
                    <Button asChild variant="outline" className="w-full h-12 rounded-lg border-primary bg-transparent text-[0.95rem] font-semibold tracking-[0.02em] text-foreground hover:bg-primary hover:text-white">
                      <Link to="#contact">Contact us for FREE consultation today</Link>
                    </Button>
                  </SheetClose>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </section>;
}